import { content } from '../scene/context.js';
import { setHover } from '../scene/gizmo.js';
import { rebuildHelpers } from '../scene/helpers.js';
import { applyLighting } from '../scene/lighting.js';
import { disposeNode, syncNode, syncParenting } from '../scene/objects.js';
import { emit, requestRender } from '../state/bus.js';
import { $, S } from '../state/store.js';
import { applyAnimation } from '../state/timeline.js';
import { reapVideoReaders } from '../scene/video.js';
import { renderRails, syncGizmoBar, updateHint } from '../ui/rails.js';

/* ═══════════════════════════════════════════════════════════
   HISTORY
   Snapshot-based rather than a command pattern. With scenes this size the memory
   cost is a few kilobytes a step, and a snapshot cannot drift out of sync with
   the live state — which is the usual way hand-written undo systems rot.

   Scope is the scene and the look, deliberately not the calibration: the solve
   sits behind an explicit button and silently rewinding someone's locked angle
   would be worse than not offering it. Image data URLs are held aside in a vault
   so they are never duplicated into the stack.
   ════════════════════════════════════════════════════════════ */
const HIST_CAP = 80;
const srcVault = new Map();
const hist = {past:[], present:null, future:[]};

function snapshot(){
  return JSON.stringify({
    objects: S.objects.map(ob=>{
      const c = {...ob}; delete c.node;
      /* Only an inline data URL is worth keeping out of the snapshot string.
       An assetId, a blobId or a signed URL is a short string, and vaulting
       those made the last-write-wins flaw below reachable for no benefit:
       the vault holds ONE value per object id, so replacing an object's
       source and then undoing twice restores the NEW source, not the old
       one. Narrowing the test leaves image/model behaviour byte-identical
       while taking video — whose src is never a data URL — out of it. */
    if(c.params && typeof c.params.src === 'string' && c.params.src.startsWith('data:')){
        srcVault.set(ob.id, c.params.src);
        c.params = {...c.params, src:'#vault'};
      }
      // A texture data URL can be megabytes; keep it out of every history entry
      // the same way, keyed per object so undo can restore the exact image.
      if(c.map && c.map.src){
        srcVault.set('map:'+ob.id, c.map.src);
        c.map = {...c.map, src:'#vault'};
      }
      return c;
    }),
    nextId:S.nextId, selId:S.selId, nudge:S.nudge,
    lighting:S.lighting, lightAz:S.lightAz, lightEl:S.lightEl,
    showHelpers:S.showHelpers, bg:S.bg, tone:S.tone, envInt:S.envInt,
    aoStrength:S.aoStrength, aoReach:S.aoReach,
    dofStrength:S.dofStrength, dofFocus:{ ...S.dofFocus },
    /* Duration and fps, because shortening the timeline can strand keyframes
       past its end and that has to be undoable. The PLAYHEAD is deliberately
       absent — see its comment in state/store.js. Keyframes themselves need
       nothing here: they live on the objects, which are already snapshotted
       whole. */
    timeline:S.timeline
  });
}

/* No-ops when nothing in the snapshot changed, so it is safe to wire this to
   every control — viewport-only settings simply never register. */
export function pushHistory(){
  if(batching) return;
  const snap = snapshot();
  if(snap === hist.present) return;
  if(hist.present !== null) hist.past.push(hist.present);
  while(hist.past.length > HIST_CAP) hist.past.shift();
  hist.present = snap;
  hist.future.length = 0;
  syncHistoryUI();
  /* Autosave listens for this rather than being called directly, which keeps
     history from importing the services layer. The early return above means a
     no-op edit never reaches the network. */
  emit('history:pushed');
}
/* Fold what has changed since the last push INTO that push, rather than adding
   a step — for one action built from calls that each push on their own (add a
   null, then link to it), so a single undo takes back all of it. */
export function amendHistory(){
  if(batching) return;
  const snap = snapshot();
  if(snap === hist.present) return;
  hist.present = snap;
  hist.future.length = 0;
  syncHistoryUI();
  emit('history:pushed');
}
/* ONE ACTION, ONE UNDO STEP, however many pushes it makes on the way — an import
   that also gives every arrival its own null makes one per null. Pushes inside
   are held, and a single one is made at the end. Async-aware, since the import
   it exists for awaits its downloads. */
let batching = 0;
export async function batchHistory(fn){
  batching++;
  try { return await fn(); }
  finally { batching--; if(!batching) pushHistory(); }
}
let histTimer;
export function pushHistorySoon(){
  clearTimeout(histTimer);
  histTimer = setTimeout(pushHistory, 400);
}
/* "Whatever is on screen right now is the starting point" — a boot, a project
   load, or the end of a setup flow that built a starter scene on the user's
   behalf. That makes it the honest place to clear S.dirty as well: nothing is
   unsaved RELATIVE TO A BASELINE that was just established.

   Without this, seeding the starter recess counted as unsaved work, so someone
   who opened the app, clicked through manual setup and left again got a "leave
   site?" prompt over a scene they had not authored a single thing in. A warning
   that fires when nothing is at stake is worse than none — it is the reason
   people dismiss the one that matters. */
export function resetHistory(){
  hist.past.length = 0; hist.future.length = 0;
  hist.present = snapshot();
  S.dirty = false;
  syncHistoryUI();
}
function applySnapshot(json){
  const d = JSON.parse(json);
  const envChanged = d.lighting !== S.lighting;
  S.objects.forEach(ob=>{ if(ob.node.parent) ob.node.parent.remove(ob.node); disposeNode(ob.node); });
  S.objects = [];
  Object.assign(S, {
    nextId:d.nextId, nudge:d.nudge, lighting:d.lighting,
    lightAz:d.lightAz, lightEl:d.lightEl,
    showHelpers:d.showHelpers,
    bg:d.bg, tone:d.tone, envInt:d.envInt,
    aoStrength:d.aoStrength ?? 0, aoReach:d.aoReach ?? S.aoReach,
    dofStrength:d.dofStrength ?? 0, dofFocus:{ ...(d.dofFocus || { mode:'screen', z:0, id:null }) },
    timeline:d.timeline || S.timeline
  });
  d.objects.forEach(src=>{
    const ob = JSON.parse(JSON.stringify(src));
    if(ob.params && ob.params.src === '#vault') ob.params.src = srcVault.get(ob.id) || '';
    if(ob.map && ob.map.src === '#vault') ob.map.src = srcVault.get('map:'+ob.id) || '';
    ob.node = null;
    S.objects.push(ob);
    syncNode(ob);
  });
  /* A child's own syncNode (above) can run before its parent's — array order
     is restore order, not parent-before-child order — so it lands on content
     until this second, order-independent pass reaches it. A no-op for
     anything already correctly attached. Also drops a parentId that doesn't
     resolve to a restored object, the same defensive posture selId gets
     right below — not reachable within one linear undo history, but a
     stale reference is safer ignored than followed. */
  S.objects.forEach(ob=>{
    if(ob.parentId != null && !S.objects.some(p=>p.id===ob.parentId)) ob.parentId = null;
  });
  S.objects.forEach(syncParenting);
  S.selId = S.objects.some(ob=>ob.id===d.selId) ? d.selId : null;
  /* An undo can delete an object, and a video object holds a hardware decoder
     that nothing else will close. Swept against the restored object list
     rather than released at each call site — a missed one leaks for the
     tab's lifetime, and the sweep cannot miss. */
  reapVideoReaders();
  /* The snapshot restored both the tracks and the pose that was evaluated
     from them, so this is usually a no-op — but not always: undoing a change
     to the timeline's DURATION can move where a track's last keyframe clamps,
     and undoing a keyframe edit has to put the scene back on the curve rather
     than on the pose that keyframe produced. */
  applyAnimation();
  /* The timeline draws keyframes and their easing, and an undo can change
     both — so it has to be told, the same way the rails are below. Without
     this the model undid correctly while the dock kept drawing the old
     shapes, which reads as "undo doesn't work on keyframes". */
  emit('anim:changed');
  rebuildHelpers();
  applyLighting(envChanged);
  setHover(null);
  renderRails(); syncGizmoBar(); updateHint(); requestRender();
}
export function undo(){
  if(!hist.past.length) return;
  hist.future.push(hist.present);
  hist.present = hist.past.pop();
  applySnapshot(hist.present);
  syncHistoryUI();
}
export function redo(){
  if(!hist.future.length) return;
  hist.past.push(hist.present);
  hist.present = hist.future.pop();
  applySnapshot(hist.present);
  syncHistoryUI();
}
/** How many undo steps are stacked up. The button that used to show this is
 *  long gone from the top bar (syncHistoryUI below still checks for it and
 *  returns), so this is the only way anything can ask — and what tells the
 *  animation tests that scrubbing the playhead adds no entries, which is the
 *  invariant most likely to break silently. */
export const historyDepth = () => hist.past.length;

function syncHistoryUI(){
  const u = $('#btnUndo'), r = $('#btnRedo');
  if(!u) return;
  u.disabled = !hist.past.length;
  r.disabled = !hist.future.length;
  u.title = hist.past.length ? `Undo — ${hist.past.length} step${hist.past.length===1?'':'s'} back` : 'Nothing to undo';
  r.title = hist.future.length ? `Redo — ${hist.future.length} available` : 'Nothing to redo';
}
/* Commit on `change`, not `input`: typing 1.234 stays one history entry, and a
   slider registers on release rather than every pixel of travel. */
export const histOn = node => { node.addEventListener('change', pushHistory); return node; };
